import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Calculator, GraduationCap, ArrowRight, Info, Loader2, Target, TrendingUp, Heart, MessageCircle, Phone, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_NUM = "7410121121";
const WHATSAPP_LINK = `https://wa.me/91${PHONE_NUM}?text=Hi,%20I%20used%20the%20Child%20Education%20Calculator%20on%20FutureSanchay%20and%20want%20to%20discuss%20plans`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const ChildEducationCalculator = () => {
  const [childAge, setChildAge] = useState(5);
  const [educationStartAge, setEducationStartAge] = useState(18);
  const [educationCost, setEducationCost] = useState(2000000);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const calculateEducation = async () => {
    if (childAge >= educationStartAge) {
      toast.error("Child's current age must be less than education start age");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/calculator/child-education`, {
        child_age: childAge,
        education_start_age: educationStartAge,
        future_education_cost: educationCost,
      });
      setResult(response.data);
      toast.success("Calculation complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Calculation failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white page-transition" data-testid="child-education-calculator-page">
      <Helmet>
        <title>Child Education Calculator | Plan Your Child's Future | FutureSanchay</title>
        <meta name="description" content="Free child education planning calculator. Calculate how much to save monthly for your child's higher education. Factor in inflation. Get expert LIC advice." />
        <link rel="canonical" href="https://futuresanchay.com/calculator/child-education" />
      </Helmet>
      {/* Hero Header */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-16 relative overflow-hidden" data-testid="calculator-hero">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-amber-50 rounded-full blur-3xl animate-pulse opacity-60"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-sky-50 rounded-full blur-3xl animate-pulse opacity-50" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="container-main relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 animate-fade-in-down">
            <Link to="/" className="hover:text-sky-500 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">Child Education Calculator</span>
          </div>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-600 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-fade-in-up">
              <GraduationCap className="w-4 h-4" />
              Free Planning Tool
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 animate-fade-in-up delay-100">
              Child Education Calculator
            </h1>
            <p className="text-lg text-gray-600 animate-fade-in-up delay-200">
              Education costs are <span className="font-semibold text-amber-500">rising faster than inflation</span>. Plan ahead to ensure your child's dreams aren't limited by finances.
            </p>
          </div>
        </div>
      </section>

      {/* Image Banner */}
      <section className="pb-8">
        <div className="container-main">
          <div className="rounded-3xl overflow-hidden shadow-xl max-w-3xl mx-auto animate-scale-in">
            <img
              src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&q=85"
              alt="Student achieving academic success"
              className="w-full h-48 md:h-64 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="pb-12 md:pb-16">
        <div className="container-main">
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Input Form */}
            <div className="lg:col-span-7 animate-fade-in-up delay-300">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-6 md:p-8 hover:shadow-2xl transition-all duration-500" data-testid="calculator-form">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Calculator className="w-5 h-5 text-amber-500" />
                  </div>
                  Enter Your Details
                </h2>

                <div className="space-y-8">
                  {/* Child Age */}
                  <div className="group">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-gray-700">Child's Current Age</Label>
                      <span className="font-bold text-2xl text-gray-900 group-hover:text-sky-500 transition-colors">{childAge} years</span>
                    </div>
                    <Slider
                      value={[childAge]}
                      onValueChange={(value) => setChildAge(value[0])}
                      min={0}
                      max={15}
                      step={1}
                      className="mb-2"
                      data-testid="child-age-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>0 years</span>
                      <span>15 years</span>
                    </div>
                  </div>

                  {/* Education Start Age */}
                  <div className="group">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium text-gray-700">Higher Education Start Age</Label>
                      <span className="font-bold text-2xl text-gray-900 group-hover:text-sky-500 transition-colors">{educationStartAge} years</span>
                    </div>
                    <Slider
                      value={[educationStartAge]}
                      onValueChange={(value) => setEducationStartAge(value[0])}
                      min={16}
                      max={22}
                      step={1}
                      className="mb-2"
                      data-testid="education-start-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>16 years</span>
                      <span>22 years</span>
                    </div>
                  </div>

                  {/* Education Cost */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-3 block">Estimated Education Cost (Today's Value)</Label>
                    <Input
                      type="number"
                      value={educationCost}
                      onChange={(e) => setEducationCost(Number(e.target.value))}
                      placeholder="Enter amount"
                      className="h-12 bg-white border-gray-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 rounded-xl text-base transition-all duration-300"
                      data-testid="education-cost-input"
                    />
                    <p className="text-xs text-gray-500 mt-2">Include tuition, living expenses, books, etc.</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {[1000000, 2000000, 5000000, 10000000].map((amount) => (
                        <button
                          key={amount}
                          onClick={() => setEducationCost(amount)}
                          className={`text-xs px-4 py-2 rounded-full transition-all duration-300 hover:scale-105 ${
                            educationCost === amount
                              ? "bg-sky-500 text-white shadow-md"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                          data-testid={`preset-${amount}`}
                        >
                          {formatCurrency(amount)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={calculateEducation}
                    disabled={isLoading}
                    className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95"
                    data-testid="calculate-btn"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Calculating...</>
                    ) : (
                      <><Sparkles className="w-5 h-5 mr-2" /> Calculate Monthly Saving</>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-5 animate-fade-in-up delay-400">
              {result ? (
                <div className="space-y-4 animate-scale-in" data-testid="calculator-result">
                  {/* Main Result Card */}
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-3xl p-6 shadow-xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 opacity-20 animate-gradient"></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="w-5 h-5 text-amber-200" />
                        <h3 className="text-amber-100 font-medium">Monthly Saving Required</h3>
                      </div>
                      <div className="text-4xl font-bold mb-2">
                        {formatCurrency(result.result.monthly_saving_needed)}
                      </div>
                      <p className="text-amber-100 text-sm">Start saving this amount monthly to reach your child's education goal.</p>
                    </div>
                  </div>

                  {/* Details Card */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-lg">
                    <h4 className="font-bold text-gray-900 mb-4">Education Planning Details</h4>
                    <div className="space-y-3">
                      {[
                        { label: "Years Until Education", value: `${result.result.years_to_goal} years` },
                        { label: "Inflated Education Cost", value: formatCurrency(result.result.inflated_education_cost) },
                        { label: "Total Investment", value: formatCurrency(result.result.total_investment) },
                        { label: "Potential Growth", value: formatCurrency(result.result.potential_growth), highlight: true },
                      ].map((item, index) => (
                        <div key={index} className="flex justify-between py-2 border-b border-gray-50 last:border-0">
                          <span className="text-gray-500">{item.label}</span>
                          <span className={`font-semibold ${item.highlight ? 'text-emerald-500' : 'text-gray-900'}`}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Card */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-lg">
                    <h4 className="font-bold text-gray-900 mb-2">Start planning today</h4>
                    <p className="text-gray-500 text-sm mb-4">LIC's child plans combine savings with life cover to protect your child's future.</p>
                    <div className="space-y-2">
                      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="block">
                        <Button className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all hover:scale-105 animate-whatsapp-pulse" data-testid="result-whatsapp-btn">
                          <MessageCircle className="w-5 h-5 mr-2" /> Chat on WhatsApp
                        </Button>
                      </a>
                      <Link to="/contact" className="block">
                        <Button variant="outline" className="w-full h-12 rounded-xl font-semibold border-2 border-sky-200 text-sky-600 hover:bg-sky-50 hover:border-sky-300 transition-all" data-testid="result-callback-btn">
                          <Phone className="w-5 h-5 mr-2" /> Request Callback
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex gap-3">
                      <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">{result.disclaimer}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl border border-gray-100 p-8 text-center shadow-lg" data-testid="calculator-placeholder">
                  <div className="w-20 h-20 bg-amber-100 rounded-3xl flex items-center justify-center mx-auto mb-4 animate-float">
                    <GraduationCap className="w-10 h-10 text-amber-500" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-xl mb-2">Your Results Will Appear Here</h3>
                  <p className="text-gray-500">Enter your child's details to see how much you need to save monthly.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Other Calculators */}
      <section className="pb-16 bg-gray-50">
        <div className="container-main py-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Try Other Calculators</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/calculator/monthly-saving" className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 shine-effect" data-testid="other-calc-monthly">
              <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-6 h-6 text-sky-500" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1 group-hover:text-sky-500 transition-colors">Monthly Saving Calculator</h4>
              <p className="text-gray-500 text-sm mb-3">See how your monthly savings can grow over time.</p>
              <span className="text-sky-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                Try now <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link to="/calculator/retirement" className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 shine-effect" data-testid="other-calc-retirement">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Target className="w-6 h-6 text-emerald-500" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1 group-hover:text-sky-500 transition-colors">Retirement Gap Calculator</h4>
              <p className="text-gray-500 text-sm mb-3">Find out if you're on track for retirement.</p>
              <span className="text-sky-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                Try now <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChildEducationCalculator;
